import type { ApiPaginatedResponse, ApiResponse, LogExportOptions } from '@bcube/shared';
import { api } from '@/lib/api';
import type { IAutoTestLog } from '@/types/production/auto-test-log';
import type { IConfigurationLog } from '@/types/production/configuration-log';
import type { IFirmwareUpdateLog } from '@/types/production/firmware-update-log';
import type { IIdentificationLog } from '@/types/production/identification-log';

// Helper function to transform date strings to Date objects
const transformDates = <T extends { created_at?: string | Date }>(obj: T): T => {
  if (!obj) return obj;
  const result = { ...obj };
  if (result.created_at && typeof result.created_at === 'string') {
    (result as any).created_at = new Date(result.created_at);
  }
  return result;
};

export interface FindManyLogsParams {
  page?: number;
  limit?: number;
  query?: string;
  serial?: string;
  user?: string;
  iccid?: string;
  profile?: string;
  status?: boolean | string;
  checked?: boolean;
  technology_id?: string;
  technology?: string;
  from?: string;
  to?: string;
  start_date?: string;
  end_date?: string;
}

/** Filtros da tela + opções escolhidas no modal de exportação. */
export type ExportLogsParams = Omit<FindManyLogsParams, 'page' | 'limit'> & LogExportOptions;

export interface PaginatedLogsResponse<T> {
  docs: T[];
  total: number;
  pages: number;
  limit: number;
}

export interface LogStats {
  total: number;
  success: number;
  failed: number;
  byTechnology?: Record<string, number>;
}

// Auto Test Logs API
export const autoTestLogApi = {
  findMany: async (params?: FindManyLogsParams): Promise<PaginatedLogsResponse<IAutoTestLog>> => {
    const { data } = await api.get<ApiPaginatedResponse<IAutoTestLog>>(
      '/production/auto-test-logs',
      { params },
    );
    return {
      docs: data.data.map(transformDates),
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<IAutoTestLog> => {
    const { data } = await api.get<ApiResponse<IAutoTestLog>>(`/production/auto-test-logs/${id}`);
    return transformDates(data.data);
  },

  getStats: async (): Promise<LogStats> => {
    const { data } = await api.get<ApiResponse<LogStats>>('/production/auto-test-logs/stats');
    return data.data;
  },

  create: async (payload: Partial<IAutoTestLog>): Promise<IAutoTestLog> => {
    const { data } = await api.post<ApiResponse<IAutoTestLog>>(
      '/production/auto-test-logs',
      payload,
    );
    return transformDates(data.data);
  },

  createBatch: async (logs: Partial<IAutoTestLog>[]): Promise<IAutoTestLog[]> => {
    const { data } = await api.post<ApiResponse<IAutoTestLog[]>>(
      '/production/auto-test-logs/batch',
      logs,
    );
    return data.data.map(transformDates);
  },
};

// Configuration Logs API
export const configurationLogApi = {
  findMany: async (
    params?: FindManyLogsParams,
  ): Promise<PaginatedLogsResponse<IConfigurationLog>> => {
    const { data } = await api.get<ApiPaginatedResponse<IConfigurationLog>>(
      '/production/configuration-logs',
      { params },
    );
    return {
      docs: data.data.map(transformDates),
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<IConfigurationLog> => {
    const { data } = await api.get<ApiResponse<IConfigurationLog>>(
      `/production/configuration-logs/${id}`,
    );
    return transformDates(data.data);
  },

  getStats: async (): Promise<LogStats> => {
    const { data } = await api.get<ApiResponse<LogStats>>('/production/configuration-logs/stats');
    return data.data;
  },

  export: async (params?: ExportLogsParams): Promise<Blob> => {
    const { data } = await api.get<Blob>('/production/configuration-logs/export', {
      params,
      responseType: 'blob',
      timeout: 120000, // 2 minutes for export
    });
    return data;
  },

  create: async (payload: Partial<IConfigurationLog>): Promise<IConfigurationLog> => {
    const { data } = await api.post<ApiResponse<IConfigurationLog>>(
      '/production/configuration-logs',
      payload,
    );
    return transformDates(data.data);
  },

  createBatch: async (logs: Partial<IConfigurationLog>[]): Promise<IConfigurationLog[]> => {
    const { data } = await api.post<ApiResponse<IConfigurationLog[]>>(
      '/production/configuration-logs/batch',
      logs,
    );
    return data.data.map(transformDates);
  },
};

// Firmware Update Logs API
export const firmwareUpdateLogApi = {
  findMany: async (
    params?: FindManyLogsParams,
  ): Promise<PaginatedLogsResponse<IFirmwareUpdateLog>> => {
    const { data } = await api.get<ApiPaginatedResponse<IFirmwareUpdateLog>>(
      '/production/firmware-update-logs',
      { params },
    );
    return {
      docs: data.data.map(transformDates),
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<IFirmwareUpdateLog> => {
    const { data } = await api.get<ApiResponse<IFirmwareUpdateLog>>(
      `/production/firmware-update-logs/${id}`,
    );
    return transformDates(data.data);
  },

  getStats: async (): Promise<LogStats> => {
    const { data } = await api.get<ApiResponse<LogStats>>('/production/firmware-update-logs/stats');
    return data.data;
  },

  export: async (params?: FindManyLogsParams): Promise<Blob> => {
    const { data } = await api.get<Blob>('/production/firmware-update-logs/export', {
      params,
      responseType: 'blob',
      timeout: 120000, // 2 minutes for export
    });
    return data;
  },

  create: async (payload: Partial<IFirmwareUpdateLog>): Promise<IFirmwareUpdateLog> => {
    const { data } = await api.post<ApiResponse<IFirmwareUpdateLog>>(
      '/production/firmware-update-logs',
      payload,
    );
    return transformDates(data.data);
  },

  createBatch: async (logs: Partial<IFirmwareUpdateLog>[]): Promise<IFirmwareUpdateLog[]> => {
    const { data } = await api.post<ApiResponse<IFirmwareUpdateLog[]>>(
      '/production/firmware-update-logs/batch',
      logs,
    );
    return data.data.map(transformDates);
  },
};

// Identification Logs API
export const identificationLogApi = {
  findMany: async (
    params?: FindManyLogsParams,
  ): Promise<PaginatedLogsResponse<IIdentificationLog>> => {
    const { data } = await api.get<ApiPaginatedResponse<IIdentificationLog>>(
      '/production/identification-logs',
      { params },
    );
    return {
      docs: data.data.map(transformDates),
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<IIdentificationLog> => {
    const { data } = await api.get<ApiResponse<IIdentificationLog>>(
      `/production/identification-logs/${id}`,
    );
    return transformDates(data.data);
  },

  getStats: async (): Promise<LogStats> => {
    const { data } = await api.get<ApiResponse<LogStats>>('/production/identification-logs/stats');
    return data.data;
  },

  export: async (params?: ExportLogsParams): Promise<Blob> => {
    const { data } = await api.get<Blob>('/production/identification-logs/export', {
      params,
      responseType: 'blob',
      timeout: 120000, // 2 minutes for export
    });
    return data;
  },

  create: async (payload: Partial<IIdentificationLog>): Promise<IIdentificationLog> => {
    const { data } = await api.post<ApiResponse<IIdentificationLog>>(
      '/production/identification-logs',
      payload,
    );
    return transformDates(data.data);
  },

  createBatch: async (logs: Partial<IIdentificationLog>[]): Promise<IIdentificationLog[]> => {
    const { data } = await api.post<ApiResponse<IIdentificationLog[]>>(
      '/production/identification-logs/batch',
      logs,
    );
    return data.data.map(transformDates);
  },
};
